import React, { useState, useRef } from 'react';
import { 
  FileText, 
  Printer, 
  Download,
  Clock,
  Award,
  CheckCircle2,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Shuffle,
  Layers,
  Settings,
  ArrowRight
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AssessmentBlueprint } from '../ai-planner/AssessmentBlueprint';
import { 
  Accordion, 
  AccordionContent, 
  AccordionItem, 
  AccordionTrigger 
} from "@/components/ui/accordion";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from 'sonner';


const TermExamGenerator = ({ 
  subject, 
  year, 
  unitTests, 
  subjectColor = "blue" 
}) => {
  const [selectedTerm, setSelectedTerm] = useState('term1');
  const [selectedStrands, setSelectedStrands] = useState([]);
  const [examDuration, setExamDuration] = useState('120');
  const [totalMarks, setTotalMarks] = useState('100');
  const [generatedExam, setGeneratedExam] = useState(null);
  const [showAnswers, setShowAnswers] = useState(false);
  const [currentStep, setCurrentStep] = useState('strands'); // 'strands' or 'settings'
  const printRef = useRef(null);

  // Get available strands from unit tests
  const getStrands = () => {
    if (!unitTests) return [];
    return Object.entries(unitTests).map(([key, value]) => ({
      id: key,
      title: value.title || key,
      tests: value.tests || []
    }));
  };

  const strands = getStrands();

  // Toggle strand selection
  const toggleStrand = (strandId) => {
    setSelectedStrands(prev => 
      prev.includes(strandId) 
        ? prev.filter(id => id !== strandId)
        : [...prev, strandId]
    );
  };

  // Generate exam from selected strands
  const generateExam = () => {
    if (selectedStrands.length === 0) {
      toast.error('Please select at least one strand');
      return;
    }

    const targetMarks = parseInt(totalMarks);
    let questions = [];
    let currentMarks = 0;

    // Collect questions from selected strands
    selectedStrands.forEach(strandId => {
      const strand = strands.find(s => s.id === strandId);
      if (strand && strand.tests) {
        strand.tests.forEach(test => {
          if (test.questions) {
            test.questions.forEach(q => {
              questions.push({
                ...q,
                strand: strand.title,
                strandId: strandId
              });
            });
          }
        });
      }
    });

    // Shuffle and select questions to reach target marks
    const shuffled = questions.sort(() => Math.random() - 0.5);
    const selected = [];
    
    for (const q of shuffled) {
      if (currentMarks + q.marks <= targetMarks) {
        selected.push(q);
        currentMarks += q.marks;
      }
      if (currentMarks >= targetMarks * 0.95) break; // Stop at 95% of target
    }

    // Sort by strand for organization
    selected.sort((a, b) => a.strandId.localeCompare(b.strandId));

    // Renumber questions
    const numbered = selected.map((q, idx) => ({
      ...q,
      examNumber: idx + 1
    }));

    setGeneratedExam({
      subject,
      year,
      term: selectedTerm.replace('term', 'Term '),
      duration: examDuration,
      totalMarks: currentMarks,
      questions: numbered,
      generatedAt: new Date().toISOString()
    });

    toast.success(`Exam generated with ${numbered.length} questions (${currentMarks} marks)`);
  };

  // Print exam
  const handlePrint = () => {
    const printContent = printRef.current;
    if (!printContent) return;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>${subject} - Year ${year} ${generatedExam?.term} Examination</title>
        <style>
          @media print {
            @page { margin: 1.5cm; size: A4; }
          }
          body { 
            font-family: 'Times New Roman', serif; 
            font-size: 12pt;
            line-height: 1.5;
            color: #000;
          }
          .exam-header { 
            text-align: center; 
            border-bottom: 2px solid #000;
            padding-bottom: 15px;
            margin-bottom: 20px;
          }
          .school-name { font-size: 16pt; font-weight: bold; margin-bottom: 5px; }
          .exam-title { font-size: 14pt; font-weight: bold; margin: 10px 0; }
          .exam-info { font-size: 11pt; margin: 5px 0; }
          .instructions { 
            border: 1px solid #000; 
            padding: 10px; 
            margin: 15px 0;
            background: #f9f9f9;
          }
          .instructions-title { font-weight: bold; margin-bottom: 5px; }
          .section { margin: 20px 0; page-break-inside: avoid; }
          .section-title { 
            font-weight: bold; 
            font-size: 13pt;
            border-bottom: 1px solid #000;
            padding-bottom: 5px;
            margin-bottom: 15px;
          }
          .question { 
            margin: 15px 0; 
            page-break-inside: avoid;
          }
          .question-header { 
            display: flex; 
            justify-content: space-between;
            margin-bottom: 5px;
          }
          .question-number { font-weight: bold; }
          .question-marks { font-style: italic; }
          .question-text { margin-left: 20px; }
          .options { margin-left: 40px; margin-top: 5px; }
          .option { margin: 3px 0; }
          .answer-space { 
            border: 1px dashed #ccc; 
            min-height: 60px; 
            margin: 10px 20px;
          }
          .answer { 
            background: #e8f5e9; 
            border-left: 3px solid #4caf50;
            padding: 10px;
            margin: 10px 20px;
          }
          .answer-label { font-weight: bold; color: #2e7d32; }
          .footer { 
            text-align: center; 
            margin-top: 30px;
            padding-top: 10px;
            border-top: 1px solid #ccc;
            font-size: 10pt;
          }
        </style>
      </head>
      <body>
        ${printContent.innerHTML}
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  // Render question based on type
  const renderQuestion = (q, showAnswer) => {
    return (
      <div key={q.examNumber} className="question">
        <div className="question-header">
          <span className="question-number">Question {q.examNumber}.</span>
          <span className="question-marks">[{q.marks} marks]</span>
        </div>
        <div className="question-text">
          <p>{q.question}</p>
          {q.type === 'multiple-choice' && q.options && (
            <div className="options">
              {q.options.map((opt, idx) => (
                <div key={idx} className="option">
                  {String.fromCharCode(65 + idx)}. {opt}
                </div>
              ))}
            </div>
          )}
          {q.type === 'matching' && q.items && (
            <div className="options">
              <p><strong>Column A:</strong> {q.items.map(i => i.term).join(', ')}</p>
              <p><strong>Column B:</strong> {q.items.map(i => i.definition).join('; ')}</p>
            </div>
          )}
        </div>
        {!showAnswer && <div className="answer-space"></div>}
        {showAnswer && (
          <div className="answer">
            <span className="answer-label">Answer: </span>
            {q.answer}
          </div>
        )}
      </div>
    );
  };

  // Group questions by strand for display
  const groupByStrand = (questions) => {
    const groups = {};
    questions.forEach(q => {
      if (!groups[q.strand]) {
        groups[q.strand] = [];
      }
      groups[q.strand].push(q);
    });
    return groups;
  };

  const colorClasses = {
    blue: 'bg-blue-600 hover:bg-blue-700',
    green: 'bg-green-600 hover:bg-green-700',
    teal: 'bg-teal-600 hover:bg-teal-700',
    amber: 'bg-amber-600 hover:bg-amber-700',
    indigo: 'bg-indigo-600 hover:bg-indigo-700',
    emerald: 'bg-emerald-600 hover:bg-emerald-700',
    purple: 'bg-purple-600 hover:bg-purple-700',
    rose: 'bg-rose-600 hover:bg-rose-700'
  };

  return (
    <div className="space-y-6">
      {/* Configuration Panel */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Term Examination Generator
          </CardTitle>
          <CardDescription>
            Step 1: Select strands, Step 2: Configure settings, then generate your exam
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Step Tabs */}
          <Tabs value={currentStep} onValueChange={setCurrentStep} className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-4">
              <TabsTrigger value="strands" className="flex items-center gap-2" data-testid="strands-tab">
                <Layers className="h-4 w-4" />
                Step 1: Select Strands
                {selectedStrands.length > 0 && (
                  <Badge variant="secondary" className="ml-1">{selectedStrands.length}</Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="settings" className="flex items-center gap-2" data-testid="settings-tab">
                <Settings className="h-4 w-4" />
                Step 2: Exam Settings
              </TabsTrigger>
            </TabsList>
            
            {/* Step 1: Strand Selection */}
            <TabsContent value="strands" className="mt-4">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Select Strands to Include in Exam</label>
                  <span className="text-sm text-muted-foreground">
                    {selectedStrands.length} of {strands.length} selected
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {strands.map(strand => (
                    <div 
                      key={strand.id}
                      className={`flex items-center space-x-3 p-4 rounded-lg border cursor-pointer transition-all ${
                        selectedStrands.includes(strand.id) 
                          ? 'bg-blue-50 border-blue-300 ring-2 ring-blue-200' 
                          : 'hover:bg-gray-50 hover:border-gray-300'
                      }`}
                      onClick={() => toggleStrand(strand.id)}
                      data-testid={`strand-${strand.id}`}
                    >
                      <Checkbox 
                        checked={selectedStrands.includes(strand.id)}
                        onCheckedChange={() => toggleStrand(strand.id)}
                      />
                      <div className="flex-1">
                        <p className="font-medium text-sm">{strand.title}</p>
                        <p className="text-xs text-muted-foreground">
                          {strand.tests?.length || 0} unit tests available
                        </p>
                      </div>
                      {selectedStrands.includes(strand.id) && (
                        <CheckCircle2 className="h-5 w-5 text-blue-500" />
                      )}
                    </div>
                  ))}
                </div>
                {strands.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    <AlertCircle className="h-8 w-8 mx-auto mb-2" />
                    <p>No unit tests available for this subject yet.</p>
                  </div>
                )}
                {selectedStrands.length > 0 && (
                  <div className="flex justify-end pt-4">
                    <Button 
                      onClick={() => setCurrentStep('settings')}
                      className={colorClasses[subjectColor] || colorClasses.blue}
                      data-testid="next-to-settings-btn"
                    >
                      Next: Configure Settings
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                  </div>
                )}
              </div>
            </TabsContent>
            
            {/* Step 2: Settings and Generate */}
            <TabsContent value="settings" className="mt-4">
              <div className="space-y-6">
                {/* Selected Strands Summary */}
                {selectedStrands.length > 0 && (
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="text-sm font-medium text-blue-800 mb-2">Selected Strands:</p>
                    <div className="flex flex-wrap gap-2">
                      {selectedStrands.map(id => {
                        const strand = strands.find(s => s.id === id);
                        return (
                          <Badge key={id} variant="secondary" className="bg-blue-100">
                            {strand?.title || id}
                          </Badge>
                        );
                      })}
                    </div>
                  </div>
                )}
                
                {selectedStrands.length === 0 && (
                  <div className="bg-amber-50 p-4 rounded-lg flex items-center gap-3">
                    <AlertCircle className="h-5 w-5 text-amber-600" />
                    <div>
                      <p className="text-sm font-medium text-amber-800">No strands selected</p>
                      <p className="text-xs text-amber-600">Please go back to Step 1 and select at least one strand.</p>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setCurrentStep('strands')}
                      className="ml-auto"
                    >
                      Select Strands
                    </Button>
                  </div>
                )}
                
                {/* Settings Row */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <label className="text-sm font-medium mb-2 block">Term</label>
                    <Select value={selectedTerm} onValueChange={setSelectedTerm}>
                      <SelectTrigger data-testid="term-select">
                        <SelectValue placeholder="Select term" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="term1">Term 1</SelectItem>
                        <SelectItem value="term2">Term 2</SelectItem>
                        <SelectItem value="term3">Term 3</SelectItem>
                        <SelectItem value="term4">Term 4</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">Duration (minutes)</label>
                    <Select value={examDuration} onValueChange={setExamDuration}>
                      <SelectTrigger data-testid="duration-select">
                        <SelectValue placeholder="Select duration" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="60">60 minutes</SelectItem>
                        <SelectItem value="90">90 minutes</SelectItem>
                        <SelectItem value="120">120 minutes (2 hours)</SelectItem>
                        <SelectItem value="150">150 minutes (2.5 hours)</SelectItem>
                        <SelectItem value="180">180 minutes (3 hours)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">Total Marks</label>
                    <Select value={totalMarks} onValueChange={setTotalMarks}>
                      <SelectTrigger data-testid="marks-select">
                        <SelectValue placeholder="Select marks" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="50">50 marks</SelectItem>
                        <SelectItem value="75">75 marks</SelectItem>
                        <SelectItem value="100">100 marks</SelectItem>
                        <SelectItem value="150">150 marks</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-end">
                    <Button 
                      onClick={generateExam}
                      disabled={selectedStrands.length === 0}
                      className={`w-full ${colorClasses[subjectColor] || colorClasses.blue}`}
                      data-testid="generate-exam-btn"
                    >
                      <Shuffle className="h-4 w-4 mr-2" />
                      Generate Exam
                    </Button>
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Generated Exam Preview */}
      {generatedExam && (
        <>
        <AssessmentBlueprint
          data={{
            title: `${generatedExam.term?.toUpperCase() || ''} EXAMINATION - ${subject}`,
            total_marks: generatedExam.totalMarks,
            time_limit: examDuration,
            slos: generatedExam.slos || [],
            questions: generatedExam.questions || [],
          }}
          metadata={{
            subject: subject,
            year_level: year,
            term: generatedExam.term,
          }}
          type="assessment"
        />
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Award className="h-5 w-5" />
                Generated Examination
              </CardTitle>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowAnswers(!showAnswers)}
                  data-testid="toggle-answers-btn"
                >
                  {showAnswers ? 'Hide Answers' : 'Show Answers'}
                </Button>
                <Button
                  onClick={handlePrint}
                  className={colorClasses[subjectColor] || colorClasses.blue}
                  data-testid="print-exam-btn"
                >
                  <Printer className="h-4 w-4 mr-2" />
                  Print Exam
                </Button>
              </div>
            </div>
            <CardDescription>
              {generatedExam.questions.length} questions • {generatedExam.totalMarks} marks • {examDuration} minutes
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Print Preview */}
            <div ref={printRef} className="bg-white p-6 border rounded-lg">
              {/* Exam Header */}
              <div className="exam-header">
                <div className="school-name">{(() => { try { return (JSON.parse(localStorage.getItem('currentUser') || '{}')?.school_name || "St Patrick's College").toUpperCase(); } catch { return "ST. PATRICK'S COLLEGE"; } })()}</div>
                <div className="exam-title">{subject.toUpperCase()} - YEAR {year}</div>
                <div className="exam-title">{generatedExam.term.toUpperCase()} EXAMINATION</div>
                <div className="exam-info">
                  <strong>Duration:</strong> {examDuration} minutes | 
                  <strong> Total Marks:</strong> {generatedExam.totalMarks}
                </div>
              </div>

              {/* Instructions */}
              <div className="instructions">
                <div className="instructions-title">INSTRUCTIONS TO CANDIDATES:</div>
                <ol className="list-decimal list-inside text-sm space-y-1">
                  <li>Write your name and class on the answer booklet provided.</li>
                  <li>Answer ALL questions in the spaces provided or in your answer booklet.</li>
                  <li>Show all working for calculation questions.</li>
                  <li>Write clearly and legibly.</li>
                  <li>Check your work before submitting.</li>
                </ol>
              </div>

              {/* Questions grouped by strand */}
              {Object.entries(groupByStrand(generatedExam.questions)).map(([strand, questions]) => (
                <div key={strand} className="section">
                  <div className="section-title">SECTION: {strand.toUpperCase()}</div>
                  {questions.map(q => renderQuestion(q, showAnswers))}
                </div>
              ))}

              {/* Footer */}
              <div className="footer">
                <p>END OF EXAMINATION</p>
                <p className="text-xs text-gray-500 mt-2">
                  Generated on {new Date(generatedExam.generatedAt).toLocaleDateString()}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        </>
      )}

      {/* Help Message */}
      {!generatedExam && (
        <Card className="bg-gray-50">
          <CardContent className="py-8 text-center">
            <AlertCircle className="h-12 w-12 mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-700 mb-2">
              Ready to Generate Term Exam
            </h3>
            <p className="text-gray-500 max-w-md mx-auto">
              Select the strands you want to include, choose exam settings, 
              and click "Generate Exam" to create a randomized term examination.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default TermExamGenerator;
